// Synthesised-doc overlay: streaming synthesis, projection tabs, copy.

import { streamComplete } from "../api/model";
import { synthesisPrompt } from "../core/prompts";
import { groundingLine, state } from "../core/state";
import { PROJECTIONS, traceText } from "../core/synthesis";
import { reviewFindings } from "../core/validation";
import { renderMarkdown } from "../lib/markdown";
import { tidyProse } from "../lib/text";
import { DOC_SECTIONS } from "../core/doc";
import { docFilledCount, templateMarkdown } from "../core/docflow";
import { $, el } from "./dom";
import { toast } from "./toast";

let projection = "prd";
let synthRun = 0; // bumping this cancels any in-flight stream (projection switch)

export async function synthesize(): Promise<void> {
  const trace = traceText();
  if (!trace.trim()) { toast("make a few decisions first, then synthesize"); return; }
  const proj = PROJECTIONS[projection]!;
  const run = ++synthRun;
  openDoc();
  renderProjTabs();

  // The Template projection is the live doc — already built, assembles in 0 ms.
  if (projection === "template") {
    const md = templateMarkdown();
    $("#docBody").innerHTML = renderMarkdown(md);
    $("#docBody").dataset.md = md;
    $("#docMeta").textContent = `Template · assembled from the live doc · 0 ms · ${docFilledCount()}/${DOC_SECTIONS.length} sections populated`;
    return;
  }
  const body = $("#docBody");
  body.innerHTML = "";
  const cursor = el("span", "doc-cursor");
  body.appendChild(cursor);
  $("#docMeta").textContent = `synthesising ${proj.label.toLowerCase()}…`;

  const prompt = synthesisPrompt(
    { idea: state.idea, grounding: groundingLine("n/a"), facts: state.facts, constraints: state.constraints },
    trace, reviewFindings(), proj.instr!,
  );

  // Coalesce renders: re-parsing the whole doc + rebuilding innerHTML on every
  // token is O(n²). Instead accumulate tokens and repaint at most once per frame.
  let acc = "";
  let pending = false;
  const modal = $("#docModal");
  const paint = () => {
    pending = false;
    const nearBottom = modal.scrollHeight - modal.scrollTop - modal.clientHeight < 80;
    body.innerHTML = renderMarkdown(acc);
    body.appendChild(cursor);
    if (nearBottom) modal.scrollTop = modal.scrollHeight;
  };
  const t0 = performance.now();
  try {
    for await (const piece of streamComplete(prompt, { max: proj.max, temp: 0.55, kind: "synthesis" })) {
      if (run !== synthRun) return; // superseded by a projection switch
      acc += piece;
      if (!pending) { pending = true; requestAnimationFrame(paint); }
    }
  } catch { if (run === synthRun) toast("synthesis interrupted"); }
  if (run !== synthRun) return;
  // Final pass: tidy generation artifacts per-line (preserving indentation) so
  // nested list structure survives.
  acc = acc.split("\n").map((line) => (line.match(/^\s*/)?.[0] ?? "") + tidyProse(line)).join("\n");
  body.innerHTML = renderMarkdown(acc);
  $("#docMeta").textContent = `${proj.label} · synthesised in ${Math.round(performance.now() - t0)} ms · ${acc.length} chars`;
  $("#docBody").dataset.md = acc;
}

export function openDoc(): void { $("#docOverlay").classList.add("show"); }
export function closeDoc(): void { synthRun++; $("#docOverlay").classList.remove("show"); }

/**
 * Projection tabs in the doc header — switching re-synthesises the same trace
 * at a different altitude (PRD §13).
 */
function renderProjTabs(): void {
  const wrap = $("#projTabs");
  wrap.innerHTML = "";
  for (const [key, p] of Object.entries(PROJECTIONS)) {
    const b = el("button", "proj-tab" + (key === projection ? " active" : ""), p.label);
    b.onclick = () => { if (key === projection) return; projection = key; void synthesize(); };
    wrap.appendChild(b);
  }
}

export function copyDoc(): void {
  const md = $("#docBody").dataset.md || "";
  void navigator.clipboard.writeText(md).then(() => toast("doc copied as markdown"));
}

export function downloadDoc(): void {
  const md = $("#docBody").dataset.md || "";
  if (!md) { toast("nothing to download yet — synthesise first"); return; }
  const blob = new Blob([md], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = el("a") as HTMLAnchorElement;
  a.href = url;
  const slug = (state.idea || "design").slice(0, 32).replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "");
  a.download = `${slug}-${projection}.md`;
  a.click();
  URL.revokeObjectURL(url);
  toast("doc downloaded as markdown");
}
